import React, { useState, useEffect, useCallback } from "react";
import { getVendorProfile, updateVendorProfile } from "../../services/api";
import { useNavigate } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import "../../vendor-panel.css";

// ── Helpers (defined OUTSIDE to prevent remount on re-render) ─────────────────

const Section = ({ title }) => (
  <div className="vp-section-title" style={{ gridColumn: "1/-1", marginTop: 8, marginBottom: 4 }}>
    {title}
  </div>
);

const Field = ({ label, name, type = "text", disabled, value, onChange }) => (
  <div className="vp-form-group">
    <label>{label}</label>
    <input
      className="vp-input"
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={label}
    />
  </div>
);

/** Shows the existing uploaded file as a preview thumbnail, plus a file input to replace it */
const FileField = ({ label, name, file, currentUrl, onChange }) => {
  const isImage = currentUrl && /\.(jpe?g|png|gif|webp|svg)$/i.test(currentUrl);
  return (
    <div className="vp-form-group">
      <label>{label}</label>

      {currentUrl && (
        <div style={{ marginBottom: 8 }}>
          {isImage ? (
            <img
              src={currentUrl}
              alt={label}
              style={{
                width: 90, height: 90, objectFit: "cover",
                borderRadius: 8, border: "1px solid #e2e8f0", display: "block",
              }}
            />
          ) : (
            <a href={currentUrl} target="_blank" rel="noreferrer"
              style={{ fontSize: 13, color: "var(--primary, #6366f1)" }}>
              View current file ↗
            </a>
          )}
        </div>
      )}

      <div className="vp-file-upload">
        <input type="file" name={name} onChange={onChange} />
        <div className="vp-file-upload-label">
          {file
            ? file.name
            : <><span>{currentUrl ? "Replace" : "Click to upload"}</span><strong> {label}</strong></>}
        </div>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const VendorProfile = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "", email: "", businessName: "", contactNumber: "",
    address: { street: "", district: "", city: "", state: "", pincode: "" },
    bankDetails: { accountHolderName: "", accountNumber: "", ifscCode: "", bankName: "", branchName: "" },
  });

  // Existing URLs from API — used only for preview, not submitted unless replaced
  const [existingUrls, setExistingUrls] = useState({
    logo: null, panCard: null, aadhaarCard: null, agreement: null,
  });

  // New files picked by user
  const [files, setFiles] = useState({ logo: null, panCard: null, aadhaarCard: null, agreement: null });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getVendorProfile();
        const p = res.data;

        setForm({
          name: p.name || "", email: p.email || "",
          businessName: p.businessName || "", contactNumber: p.contactNumber || "",
          address: p.address || { street: "", district: "", city: "", state: "", pincode: "" },
          bankDetails: p.bankDetails || { accountHolderName: "", accountNumber: "", ifscCode: "", bankName: "", branchName: "" },
        });

        // Map nested API structure → flat URL map
        setExistingUrls({
          logo:        p.logo?.url || null,
          panCard:     p.documents?.panCard?.url || null,
          aadhaarCard: p.documents?.aadhaarCard?.url || null,
          agreement:   p.agreement?.url || null,
        });
      } catch {
        alert("Failed to load profile.");
      } finally {
        setFetching(false);
      }
    })();
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const key = name.split(".")[1];
      setForm((prev) => ({ ...prev, address: { ...prev.address, [key]: value } }));
    } else if (name.startsWith("bankDetails.")) {
      const key = name.split(".")[1];
      setForm((prev) => ({ ...prev, bankDetails: { ...prev.bankDetails, [key]: value } }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleFileChange = useCallback((e) => {
    const { name, files: f } = e.target;
    if (f?.[0]) setFiles((prev) => ({ ...prev, [name]: f[0] }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, typeof value === "object" ? JSON.stringify(value) : value);
      });
      Object.entries(files).forEach(([key, file]) => { if (file) formData.append(key, file); });
      await updateVendorProfile(formData);
      alert("Profile updated!");
      navigate("/vendor/dashboard");
    } catch {
      alert("Error updating profile");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <VendorLayout><div className="vp-loading">Loading profile...</div></VendorLayout>;

  // Avatar: real logo or initials fallback
  const initials = form.name
    ? form.name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : "V";

  return (
    <VendorLayout vendorName={form.name}>
      <div className="vp-card">
        <div className="vp-card-header">

          {/* ── Profile Avatar ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%", overflow: "hidden",
              border: "3px solid var(--primary, #6366f1)",
              background: "#e2e8f0", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 24, fontWeight: 700, color: "var(--primary, #6366f1)",
            }}>
              {existingUrls.logo
                ? <img src={existingUrls.logo} alt="logo"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : initials}
            </div>
            <div>
              <div className="vp-card-title" style={{ marginBottom: 2 }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                {form.name || "Vendor Profile"}
              </div>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {form.businessName || "Update your business information"}
              </span>
            </div>
          </div>

        </div>

        <div className="vp-card-body">
          <form onSubmit={handleSubmit}>
            <div className="vp-form-grid">

              <Section title="Basic Information" />
              <Field label="Full Name"      name="name"          value={form.name}          onChange={handleChange} />
              <Field label="Email Address"  name="email"         value={form.email}         onChange={handleChange} type="email" disabled />
              <Field label="Business Name"  name="businessName"  value={form.businessName}  onChange={handleChange} />
              <Field label="Contact Number" name="contactNumber" value={form.contactNumber} onChange={handleChange} />

              <Section title="Business Address" />
              <Field label="Street"   name="address.street"   value={form.address.street}   onChange={handleChange} />
              <Field label="District" name="address.district" value={form.address.district} onChange={handleChange} />
              <Field label="City"     name="address.city"     value={form.address.city}     onChange={handleChange} />
              <Field label="State"    name="address.state"    value={form.address.state}    onChange={handleChange} />
              <Field label="Pincode"  name="address.pincode"  value={form.address.pincode}  onChange={handleChange} />

              <Section title="Bank Details" />
              <Field label="Account Holder Name" name="bankDetails.accountHolderName" value={form.bankDetails.accountHolderName} onChange={handleChange} />
              <Field label="Account Number"      name="bankDetails.accountNumber"      value={form.bankDetails.accountNumber}      onChange={handleChange} />
              <Field label="IFSC Code"           name="bankDetails.ifscCode"           value={form.bankDetails.ifscCode}           onChange={handleChange} />
              <Field label="Bank Name"           name="bankDetails.bankName"           value={form.bankDetails.bankName}           onChange={handleChange} />
              <Field label="Branch Name"         name="bankDetails.branchName"         value={form.bankDetails.branchName}         onChange={handleChange} />

              <Section title="Documents & Logo" />
              <FileField label="Logo"             name="logo"        file={files.logo}        currentUrl={existingUrls.logo}        onChange={handleFileChange} />
              <FileField label="PAN Card"         name="panCard"     file={files.panCard}     currentUrl={existingUrls.panCard}     onChange={handleFileChange} />
              <FileField label="Aadhaar Card"     name="aadhaarCard" file={files.aadhaarCard} currentUrl={existingUrls.aadhaarCard} onChange={handleFileChange} />
              <FileField label="Vendor Agreement" name="agreement"   file={files.agreement}   currentUrl={existingUrls.agreement}   onChange={handleFileChange} />

            </div>

            <div style={{ marginTop: 24, display: "flex", gap: 10 }}>
              <button type="submit" className="vp-btn vp-btn-primary vp-btn-lg" disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </button>
              <button type="button" className="vp-btn vp-btn-ghost vp-btn-lg" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </VendorLayout>
  );
};

export default VendorProfile;